import { memo, useMemo } from 'react'
import { Table, Space, Popconfirm } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Player, PlayerFoot } from 'types/players'
import dayjs from 'dayjs'
import { ExpandableRow } from './expandable-row'
import { TeamsExpandableRow } from './teams-expandable-row'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

const FOOT_LABELS: Record<PlayerFoot, string> = {
    left: 'Левая',
    right: 'Правая',
    both: 'Обе',
}

type Props = {
    data: Player[]
    onEdit: (id: number) => void
    onDelete: (id: number) => void
    loading: boolean
}

export const PlayersTable = memo((props: Props) => {
    const { data, onEdit, onDelete, loading } = props

    const columns: ColumnsType<Player> = useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Surname',
                dataIndex: 'surname',
                key: 'surname',
            },
            {
                title: 'E-mail',
                dataIndex: 'email',
                key: 'email',
            },
            {
                title: 'Phone',
                dataIndex: 'phone',
                key: 'phone',
            },
            {
                title: 'Instagram',
                dataIndex: 'instagram',
                key: 'instagram',
                render: (_, record) => record.instagram ?? '—',
            },
            {
                title: 'Telegram',
                dataIndex: 'telegram',
                key: 'telegram',
                render: (_, record) => record.telegram ?? '—',
            },
            {
                title: 'Birthday',
                key: 'birthday',
                render: (_, record) => record.birthday && dayjs(record.birthday).format('DD.MM.YYYY'),
            },
            {
                title: 'Position',
                key: 'id_position',
                render: (_, record) => record.position?.name,
            },
            {
                title: 'Height / Weight',
                key: 'height_weight',
                render: (_, record) => {
                    const parts = []
                    if (record.height) parts.push(`${record.height} cm`)
                    if (record.weight) parts.push(`${record.weight} kg`)
                    return parts.join(' / ') || '—'
                },
            },
            {
                title: 'Foot',
                key: 'working_foot',
                render: (_, record) => (record.working_foot ? FOOT_LABELS[record.working_foot] : '—'),
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Space size='middle'>
                        <a onClick={() => onEdit(record.id)}>Edit</a>
                        <Popconfirm
                            title={formatModalTitle('Delete player', `${record.surname} ${record.name}`)}
                            description={'Are you sure to delete this player?'}
                            onConfirm={() => onDelete(record.id)}
                            okText={'Yes'}
                            cancelText={'No'}
                        >
                            <a href='#'>Delete</a>
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [onDelete, onEdit]
    )

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey='id'
            pagination={false}
            expandable={{
                expandedRowRender: record => (
                    <>
                        {!!record.playersToPlayersRatings?.length && (
                            <>
                                <div style={{ marginBottom: 8, fontWeight: 600 }}>Ratings</div>
                                <ExpandableRow data={record.playersToPlayersRatings} />
                            </>
                        )}
                        {!!record.playersToTeams?.length && (
                            <>
                                <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600 }}>Teams in leagues</div>
                                <TeamsExpandableRow data={record.playersToTeams} />
                            </>
                        )}
                    </>
                ),
                rowExpandable: record => !!record.playersToPlayersRatings?.length || !!record.playersToTeams?.length,
            }}
        />
    )
})
